'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useUserResourceSource,
  useBlockPart,
  type MediaItem,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AnonymousFallback } from '../islands/_shared';
import { sectionPad, sectionInner } from '../_shared/styles';
import { VideoCard } from './VideoCard';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * /library — the signed-in user's watch-later / history / subscriptions
 * surface. Three tabs delegate to the appropriate {@code UserResourceSource}
 * namespace; {@code initialTab} picks the active tab so the dedicated
 * watch-later / history / subscriptions routes each land on theirs.
 */
export type LibraryTab = 'watch-later' | 'liked' | 'history' | 'subscriptions';

export interface LibraryClientProps {
  initialTab?: LibraryTab;
  videoHrefTemplate?: string;
  channelHrefTemplate?: string;
}

/** Tab strip, built per render so a language switch relabels it. */
const libraryTabs = (t: (key: string) => string): { key: LibraryTab; label: string }[] => [
  { key: 'watch-later', label: t('library.tab.watchLater') },
  { key: 'liked', label: t('library.tab.liked') },
  { key: 'history', label: t('library.tab.history') },
  { key: 'subscriptions', label: t('library.tab.subscriptions') },
];

export function LibraryClient({
  initialTab = 'watch-later',
  videoHrefTemplate = '/tube/{slug}',
  channelHrefTemplate = '/tube/channels/{slug}',
}: LibraryClientProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();

  if (!user) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AnonymousFallback variant="card" prompt={t('library.signInPrompt')} />
        </div>
      </BlockRoot>
    );
  }

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        <h1
          {...part('title')}
          style={{ margin: '0 0 24px', fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700 }}
        >
          {t('library.title')}
        </h1>
        <Tabs
          initialTab={initialTab}
          ur={ur}
          videoHrefTemplate={videoHrefTemplate}
          channelHrefTemplate={channelHrefTemplate}
        />
      </div>
    </BlockRoot>
  );
}

function Tabs({
  initialTab,
  ur,
  videoHrefTemplate,
  channelHrefTemplate,
}: {
  initialTab: LibraryTab;
  ur: ReturnType<typeof useUserResourceSource>;
  videoHrefTemplate: string;
  channelHrefTemplate: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const [active, setActive] = useState<LibraryTab>(initialTab);
  return (
    <div>
      <div
        role="tablist"
        aria-label={t('library.tabsLabel')}
        {...part('tablist')}
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 20,
          borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.12))',
        }}
      >
        {libraryTabs(t).map((tab, i) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active === tab.key}
            onClick={() => setActive(tab.key)}
            {...part('tab', i)}
            style={{
              appearance: 'none',
              background: 'none',
              border: 'none',
              borderBottom:
                active === tab.key
                  ? '2px solid var(--color-brand-primary, #0a0a0a)'
                  : '2px solid transparent',
              marginBottom: -1,
              padding: '8px 12px',
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: active === tab.key ? 600 : 500,
              color:
                active === tab.key
                  ? 'var(--color-text-primary, #0a0a0a)'
                  : 'var(--color-text-secondary, #71717a)',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {active === 'watch-later' && <WatchLaterList ur={ur} videoHrefTemplate={videoHrefTemplate} />}
      {active === 'liked' && <LikedList ur={ur} videoHrefTemplate={videoHrefTemplate} />}
      {active === 'history' && <HistoryList ur={ur} videoHrefTemplate={videoHrefTemplate} />}
      {active === 'subscriptions' && <SubsList ur={ur} channelHrefTemplate={channelHrefTemplate} />}
    </div>
  );
}

function WatchLaterList({
  ur,
  videoHrefTemplate,
}: {
  ur: ReturnType<typeof useUserResourceSource>;
  videoHrefTemplate: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const state = useAsyncResource<MediaItem[]>(async () => {
    if (!ur?.watchLater) return null;
    const r = await ur.watchLater.list();
    return r.items.length > 0 ? r.items : null;
  }, [ur]);
  if (!ur?.watchLater)
    return (
      <AdapterPlaceholder blockType="WatchLater list" adapterName="UserResourceSource.watchLater" />
    );
  if (state.kind === 'loading') return <SkeletonRow count={8} layout="grid" />;
  if (state.kind === 'error') return <div style={muted}>{t('library.watchLater.error')}</div>;
  if (state.kind !== 'loaded') return <div style={muted}>{t('library.watchLater.empty')}</div>;
  return (
    <div style={grid} {...part('list')}>
      {state.data.map((v) => (
        <VideoCard key={v.id} item={v} hrefTemplate={videoHrefTemplate} />
      ))}
    </div>
  );
}

function LikedList({
  ur,
  videoHrefTemplate,
}: {
  ur: ReturnType<typeof useUserResourceSource>;
  videoHrefTemplate: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const state = useAsyncResource<MediaItem[]>(async () => {
    if (!ur?.likes) return null;
    const r = await ur.likes.list();
    return r.items.length > 0 ? r.items : null;
  }, [ur]);
  if (!ur?.likes)
    return <AdapterPlaceholder blockType="Liked list" adapterName="UserResourceSource.likes" />;
  if (state.kind === 'loading') return <SkeletonRow count={8} layout="grid" />;
  if (state.kind === 'error') return <div style={muted}>{t('library.liked.error')}</div>;
  if (state.kind !== 'loaded') return <div style={muted}>{t('library.liked.empty')}</div>;
  return (
    <div style={grid} {...part('list')}>
      {state.data.map((v) => (
        <VideoCard key={v.id} item={v} hrefTemplate={videoHrefTemplate} />
      ))}
    </div>
  );
}

function HistoryList({
  ur,
  videoHrefTemplate,
}: {
  ur: ReturnType<typeof useUserResourceSource>;
  videoHrefTemplate: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const state = useAsyncResource<MediaItem[]>(async () => {
    if (!ur?.history) return null;
    const r = await ur.history.list({ limit: 50 });
    return r.items.length > 0 ? r.items : null;
  }, [ur]);
  if (!ur?.history)
    return <AdapterPlaceholder blockType="History list" adapterName="UserResourceSource.history" />;
  if (state.kind === 'loading') return <SkeletonRow count={8} layout="grid" />;
  if (state.kind === 'error') return <div style={muted}>{t('library.history.error')}</div>;
  if (state.kind !== 'loaded') return <div style={muted}>{t('library.history.empty')}</div>;
  return (
    <div style={grid} {...part('list')}>
      {state.data.map((v) => (
        <VideoCard key={v.id} item={v} hrefTemplate={videoHrefTemplate} />
      ))}
    </div>
  );
}

function SubsList({
  ur,
  channelHrefTemplate,
}: {
  ur: ReturnType<typeof useUserResourceSource>;
  channelHrefTemplate: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const state = useAsyncResource(async () => {
    if (!ur?.subscriptions) return null;
    const r = await ur.subscriptions.list();
    return r.items.length > 0 ? r.items : null;
  }, [ur]);
  if (!ur?.subscriptions)
    return (
      <AdapterPlaceholder
        blockType="Subscriptions list"
        adapterName="UserResourceSource.subscriptions"
      />
    );
  if (state.kind === 'loading') return <SkeletonRow count={4} layout="list" />;
  if (state.kind === 'error') return <div style={muted}>{t('library.subscriptions.error')}</div>;
  if (state.kind !== 'loaded') return <div style={muted}>{t('library.subscriptions.empty')}</div>;
  return (
    <ul style={{ ...grid, listStyle: 'none', padding: 0 }} {...part('list')}>
      {state.data.map((c, i) => (
        <li key={c.id} {...part('item', i)}>
          <Link
            href={channelHrefTemplate.replace('{slug}', encodeURIComponent(c.slug))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <div
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: c.avatarUrl
                  ? `url(${c.avatarUrl}) center/cover no-repeat`
                  : 'var(--color-neutral-200, #e4e4e7)',
              }}
            />
            <span style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 500 }}>
              {c.name ?? c.slug}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
  gap: 12,
};
const muted = {
  color: 'var(--color-text-secondary, #71717a)',
  fontSize: 'var(--font-size-sm, 14px)',
};
